import { getCookie } from '@vfde-sails/utils';
import {
    Cookies,
    TvConnectGroup,
} from '../enum';
import {
    AdditionalPageOptions,
    CmsTariffBenefit,
    ImageHeaderData,
    JarvisProduct,
    PsPromoData,
} from '../interface';

/**
 * Get CMS config from additionalPageOptions
 */
export const getCMSConfig = () => window.additionalPageOptions as AdditionalPageOptions;

/**
 * Returns all tariffIds
 *
 * @returns Tariff ids
 */
export const getAllTariffIds = (): string[] => [
    ...Object.values(getCMSConfig().tariffIds.kdg),
    ...Object.values(getCMSConfig().tariffIds.ume),
];

const getBenefitsData = (tariffId: string) =>
    Object.values(getCMSConfig().tariffBenefits)
        .filter(benefit => Object.values(benefit.tariffs).includes(tariffId));

const getBenefitsByAvailability = (isSelfInstall: boolean, isTvExisting: boolean, isTvAvailable: boolean, isMinorityNE4: boolean)=> {
    const { existingTv, selfInstall, tvAvailable, changeBenefit } = getCMSConfig().tariffBenefitsWithAvailability || {};

    const benefits: CmsTariffBenefit[] = [];

    if (isSelfInstall && selfInstall) {
        benefits.push(selfInstall);
    }

    if (!isMinorityNE4 && changeBenefit) {
        benefits.push(changeBenefit);
    }

    if (isTvExisting && existingTv) {
        benefits.push(existingTv);
    }

    if (isTvAvailable && tvAvailable) {
        benefits.push(tvAvailable);
    }

    return benefits;
};

/**
 * Sorts benefits array by ordering simple benefits without a subline to the front
 *
 * @param benefits - Benefits to sort
 */
const sortBenefitsBySubline = (benefits: CmsTariffBenefit[]) =>
    benefits.sort((a, b) => (!a.subline && b.subline)
        ? -1
        : (!b.subline && a.subline)
            ? 1
            : 0,
    );

/**
 * Returns all benefits from cms based on selected product
 *
 * @param tariff - Selected tariff
 */
export const getAvailableTariffBenefits = (
    tariff: JarvisProduct,
    isSelfInstall: boolean,
    isTvExisting: boolean,
    isTvAvailable: boolean,
) => sortBenefitsBySubline([
    ...getBenefitsData(tariff.id),
    ...getBenefitsByAvailability(isSelfInstall, isTvExisting, isTvAvailable, Boolean(tariff.minorityNE4Discount)),
]);

/**
 * Returns benefit cms configs for which footnote link is configured
 */
export const getTariffBenefitsWithFootnoteLink = () => [
    ...Object.values(getCMSConfig().tariffBenefits),
    ...Object.values(getCMSConfig().tariffBenefitsWithAvailability || []),
].filter(benefit => benefit.footnoteNumber);

/**
 * Returns correct price list link based on product
 *
 * @param isUnity - If selected tariff is unity or vkd
 */
export const getPriceListLink = (isUnity: boolean) => isUnity
    ? getCMSConfig().tariffPriceListUrls.um
    : getCMSConfig().tariffPriceListUrls.vkd;

/**
 * Returns if young promo is set to active in cms
 */
export const isYoungPromoActive = () => getCMSConfig().isYoungPromoActive === 'true';

/**
 * Returns if playstation promo is set to active in cms
 */
export const isPlaystationPromoActive = () => getCMSConfig().config?.isPlaystationPromoActive === 'true';

/**
 * Returns the URL of the corresponding TV Connect group, or
 * null if there is no available TV product
 *
 * @returns Footnote URL or null
 */
export const getTvFootnoteUrl = (tvGroup: TvConnectGroup) => {
    switch (tvGroup) {
        case TvConnectGroup.START:
            return getCMSConfig().footnoteUrls.tvConnectStart;
        case TvConnectGroup.START_PRESALE:
            return getCMSConfig().footnoteUrls.tvConnectStartPresale;
        case TvConnectGroup.STANDARD:
        case TvConnectGroup.STANDARD_PRESALE:
            return getCMSConfig().footnoteUrls.tvConnectStandard;
        case TvConnectGroup.ZUHAUSE:
            return getCMSConfig().footnoteUrls.tvConnectZuhause;
        case TvConnectGroup.ZUHAUSE_PRESALE:
            return getCMSConfig().footnoteUrls.tvConnectZuhausePresale;
    }
};

/**
 * Returns the unformatted CMS text for legal text discounts
 */
export const getLegalDiscountText = (isFriendsAndFamily: boolean, isMinorityNE4: boolean) => {
    if (isMinorityNE4 && isFriendsAndFamily) {
        return getCMSConfig().offerSummaryCard.legalText.fnfNe4;
    }

    if (isMinorityNE4 && !isFriendsAndFamily) {
        return getCMSConfig().offerSummaryCard.legalText.ne4;
    }

    if (!isMinorityNE4 && isFriendsAndFamily) {
        return getCMSConfig().offerSummaryCard.legalText.fnf;
    }
};

/**
 * Returns the footnote number for the discount
 */
export const getDiscountFootnoteNumber = () => getCMSConfig().offerSummaryCard.legalText.discountFootnoteNumber;

/**
 * Returns the footnote number with TV
 */
export const getDiscountFootnoteNumberWithTv = () => getCMSConfig().offerSummaryCard.legalText.discountFootnoteNumberWithTv;

/**
 * get Image Header Data
 */
export const getImageHeaderData = (cableMax1000: boolean, isYoung?: boolean): ImageHeaderData | undefined => {
    const { imageHeader } = getCMSConfig();

    if (isYoung) {
        return imageHeader?.young;
    }

    if (cableMax1000) {
        return imageHeader?.cablemaxPlaystation;
    }

    return imageHeader?.cableAndFnf;
};

/**
 * get Ps PromoCard Data
 */
export const getPsPromoCardData = (): PsPromoData | undefined => {
    const { playstationPromoCard } = getCMSConfig();

    if (getCookie(Cookies.TELLJA)) {
        return playstationPromoCard?.afterCashbackRegistration;
    }

    return playstationPromoCard?.default;
};
